import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";
export const API = `${BASE}/api`;

export const apiClient = axios.create({
  baseURL: API,
  // Session 6.5: same-origin cookies are sent automatically by the browser.
  // We deliberately do NOT set `withCredentials: true` because the Emergent
  // edge rewrites `Access-Control-Allow-Origin: *`, which browsers reject
  // when credentials are requested cross-origin. Since frontend + backend
  // share a host here (ingress `/api/*` proxy), same-origin sends the
  // httpOnly `ff_refresh` cookie without any CORS involvement.
  withCredentials: false,
});

apiClient.interceptors.request.use((config) => {
  // One-time migration: an earlier build of GoogleCallback.jsx wrote the token
  // under "access_token". Migrate those clients to "ff_token" silently so they
  // don't have to re-login after redeploy.
  if (!localStorage.getItem("ff_token")) {
    const legacy = localStorage.getItem("access_token");
    if (legacy) {
      localStorage.setItem("ff_token", legacy);
      localStorage.removeItem("access_token");
    }
  }
  const token = localStorage.getItem("ff_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ────────────────────────────────────────────────────────────
// Session 6.5 — refresh via httpOnly cookie, with a one-time legacy
// fallback for users who still have `ff_refresh` in localStorage from
// before this ship. The fallback runs at most once per session and
// upgrades the client into the cookie world silently.
// ────────────────────────────────────────────────────────────
let _refreshPromise = null;
const AUTH_PATH_RE = /\/api\/auth\/(login|register|refresh|forgot-password|reset-password)/;

async function attemptRefresh() {
  // Same-origin request. If a cookie is present, the browser attaches it.
  // Empty body = "use the cookie". Backend also accepts a body token as
  // fallback (for legacy migration + mobile clients).
  try {
    // Explicit `withCredentials: true` on this ONE call — safe because
    // frontend + backend are same-origin here, and the browser will send
    // the ff_refresh cookie without a CORS preflight either way.
    const { data } = await axios.post(
      `${API}/auth/refresh`, {},
      { withCredentials: true }
    );
    if (data?.token) localStorage.setItem("ff_token", data.token);
    // Clean up any legacy refresh token — cookie is now the source of truth.
    localStorage.removeItem("ff_refresh");
    return data?.token || null;
  } catch (e) {
    // Cookie missing / invalid. Try legacy localStorage token exactly once.
    const legacy = localStorage.getItem("ff_refresh");
    if (!legacy) return null;
    try {
      const { data } = await axios.post(
        `${API}/auth/refresh`,
        { refresh_token: legacy },
        { withCredentials: true }
      );
      if (data?.token) localStorage.setItem("ff_token", data.token);
      // Migration complete — cookie set by backend, drop localStorage entry.
      localStorage.removeItem("ff_refresh");
      return data?.token || null;
    } catch {
      // Both cookie AND legacy token rejected. Clean logout — clear all
      // stored auth state so the next protected call surfaces the login
      // screen instead of an infinite refresh loop.
      localStorage.removeItem("ff_refresh");
      localStorage.removeItem("ff_token");
      return null;
    }
  }
}

apiClient.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const url = original.url || "";

    // Skip the retry loop for the auth endpoints themselves.
    if (status === 401 && !original._retried && !AUTH_PATH_RE.test(url)) {
      original._retried = true;
      // Coalesce concurrent refreshes so a burst of 401s only triggers
      // one round-trip to /auth/refresh.
      if (!_refreshPromise) {
        _refreshPromise = attemptRefresh().finally(() => { _refreshPromise = null; });
      }
      const newToken = await _refreshPromise;
      if (newToken) {
        original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
        return apiClient(original);
      }
    }
    return Promise.reject(err);
  }
);

export default apiClient;
